import { Text } from "@chakra-ui/react";

/**
 * How audit log entries read, wherever they are shown: the sys admin's Audit
 * log and an activity's own History. One place, so the two never describe the
 * same entry in different words.
 */

const MODULE_LABELS: Record<string, string> = {
  activity: "Catalogue",
  activityTag: "Catalogue tag",
  personActivity: "Activity",
  eventSeries: "Series",
  event: "Event",
  participation: "Participation",
  directory: "Directory",
  affiliation: "Affiliation",
  role: "Role",
};

export function moduleLabel(module: string): string {
  return MODULE_LABELS[module] ?? module;
}

/** "personActivity.taskSubmitted" -> { module: "Activity", verb: "step submitted" }. */
export function actionLabel(action: string): { module: string; verb: string } {
  const [module = "", verb = ""] = action.split(".");
  return {
    module: moduleLabel(module),
    // People read "step"; the stored action names keep the code's word "task"
    // ("personActivity.taskSubmitted"), which is data and is not renamed.
    verb: verb.replace(/([A-Z])/g, " $1").toLowerCase().replace(/\btask\b/, "step"),
  };
}

export function formatAuditTime(at: number): string {
  return new Date(at).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * A step's name, set apart from the sentence around it.
 *
 * Not a link: the step lives on the activity page, which the activity's name
 * already opens. Italic rather than underlined, so the two never look like the
 * same kind of thing.
 *
 * An `<em>`, because that is what it is: the one name in the sentence the
 * reader's eye should land on. The look is decided here and nowhere else --
 * the stored entry holds only the name, so restyling every step name in the
 * log is a change to this component, not to any data.
 */
export function StepName({ title }: { title: string }) {
  return (
    <Text as="em" fontStyle="italic">
      {title}
    </Text>
  );
}
